import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { businessApi } from '../../api';

interface StripeAccountInfo {
    id: string;
    charges_enabled: boolean;
    payouts_enabled: boolean;
    requirements: {
        currently_due: string[];
        eventually_due: string[];
        past_due: string[];
    };
    capabilities: {
        card_payments: string;
        transfers: string;
    };
    business_type: string;
    country: string;
    email: string;
}

const StripeAccountInfo: React.FC<{ businessId: string, facilityId: string }> = ({ businessId, facilityId }) => {
    const [accountInfo, setAccountInfo] = useState<StripeAccountInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();

    useEffect(() => {
        const fetchAccountInfo = async () => {
            if (!businessId || !facilityId) {
                setError('No facility ID found');
                setLoading(false);
                return;
            }

            try {
                const response = await businessApi.getStripeAccountInfo(businessId, facilityId);
                setAccountInfo(response.data);
            } catch (err) {
                setError('Failed to fetch Stripe account information');
                console.error('Error fetching Stripe account info:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchAccountInfo();
    }, [businessId, facilityId]);

    if (loading) {
        return <div className="text-center py-8">Loading account information...</div>;
    }

    if (error) {
        return <div className="text-center text-red-600 py-8">{error}</div>;
    }

    if (!accountInfo) {
        return <div className="text-center py-8">No Stripe account information available</div>;
    }

    return (
        <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Stripe Account Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm text-gray-500">Account ID</p>
                        <p className="font-medium">{accountInfo.id}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Business Type</p>
                        <p className="font-medium capitalize">{accountInfo.business_type}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Country</p>
                        <p className="font-medium">{accountInfo.country.toUpperCase()}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium">{accountInfo.email}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Last Updated</p>
                        <p className="font-medium text-gray-400 italic">Not available</p>
                    </div>
                </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Account Status</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm text-gray-500">Charges Enabled</p>
                        <p className={`font-medium ${accountInfo.charges_enabled ? 'text-green-600' : 'text-red-600'}`}>{accountInfo.charges_enabled ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Payouts Enabled</p>
                        <p className={`font-medium ${accountInfo.payouts_enabled ? 'text-green-600' : 'text-red-600'}`}>{accountInfo.payouts_enabled ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Card Payments Capability</p>
                        <p className={`font-medium ${accountInfo.capabilities.card_payments === 'active' ? 'text-green-600' : 'text-red-600'}`}>{accountInfo.capabilities.card_payments === 'active' ? 'Enabled' : 'Disabled'}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Transfers Capability</p>
                        <p className={`font-medium ${accountInfo.capabilities.transfers === 'active' ? 'text-green-600' : 'text-red-600'}`}>{accountInfo.capabilities.transfers === 'active' ? 'Enabled' : 'Disabled'}</p>
                    </div>
                </div>
            </div>

            {accountInfo.requirements.currently_due.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <h2 className="text-xl font-semibold mb-4 text-yellow-800">Requirements</h2>
                    <p className="text-yellow-700 mb-2">The following requirements need to be completed:</p>
                    <ul className="list-disc list-inside text-yellow-700">
                        {accountInfo.requirements.currently_due.map((requirement, index) => (
                            <li key={index}>{requirement}</li>
                        ))}
                    </ul>
                    <div className="mt-4">
                      <a
                        href="https://dashboard.stripe.com/settings/account"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline font-medium"
                      >
                        Go to Stripe Dashboard to resolve
                      </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StripeAccountInfo; 